import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MDText } from 'i18n-react';

import Text from '@adsk/bim360-matrix-react-text';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';

import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';

import imagePlaceholder from '@adsk/bim360-matrix-react-assets/image-placeholder.svg';
import imageError from '@adsk/bim360-matrix-react-assets/image-error-icn.svg';

import './_ThumbnailPicture.scss';

export class ThumbnailPicture extends React.Component {
  constructor(props) {
    super(props);

    this.state = this.resetState(props);
  }

  resetState(props) {
    return {
      renderPlaceholder: props.picture.error || !props.picture.src,
      isWide: false,
      error: props.picture.error
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.picture.error !== this.props.picture.error ||
      nextProps.picture.src !== this.props.picture.src
    ) {
      this.setState(this.resetState(nextProps));
    }
  }

  _renderPlaceholder() {
    const props = this.state.error
      ? {
        height: '35px',
        width: '35px',
        children: imageError,
        className: 'ThumbnailPicture__placeholder'
      }
      : {
        height: '30px',
        width: '30px',
        children: imagePlaceholder,
        className: 'ThumbnailPicture__placeholder'
      };
    return <SvgIcon {...props} />;
  }

  _renderImage() {
    const scalingAttribute =
      (this.props.crop && this.state.isWide) || (!this.props.crop && !this.state.isWide)
        ? 'height'
        : 'width';
    const style = { [scalingAttribute]: this.props.sideLength };
    const imageClasses = classNames('ThumbnailPicture__image', {
      'ThumbnailPicture__image--clickable': !!this.props.picture.onClick
    });

    return (
      <img
        style={style}
        className={imageClasses}
        data-testid="ThumbnailPicture__image--clickable"
        onClick={this.props.picture.onClick}
        onLoad={({ target }) => {
          this.setState({ isWide: target.naturalWidth >= target.naturalHeight });
          this.props.picture.onLoad && this.props.picture.onLoad();
        }}
        onError={() => this.setState({ renderPlaceholder: true, error: true })}
        src={this.props.picture.src}
      />
    );
  }

  render() {
    const imageContainerStyle = this.state.renderPlaceholder
      ? { width: this.props.sideLength, height: this.props.sideLength }
      : { maxWidth: this.props.sideLength, maxHeight: this.props.sideLength };
    const imageContainerClasses = classNames('ThumbnailPicture__image-container', {
      'ThumbnailPicture__image-container--placeholder': this.state.renderPlaceholder
    });
    const nameContainerClasses = classNames('ThumbnailPicture__name', {
      'ThumbnailPicture__name--error': this.state.error
    });
    let thumbnailLabel = null;

    if (!this.props.hideLabel) {
      thumbnailLabel = !this.state.error
        ? this.props.picture.name
        : this.props.errorText || this.props[TSymbolProp].translate('THUMBNAIL_ERROR');
    }

    const componentClasses = classNames(
      'ThumbnailPicture',
      this.props.className,
      this.state.error && this.props.picture.errorProps && this.props.picture.errorProps.className
    );
    const componentExtraProps = this.state.error ? this.props.picture.errorProps : undefined;
    return (
      <div className={componentClasses} {...componentExtraProps}>
        <div className={imageContainerClasses} style={imageContainerStyle}>
          {this.state.renderPlaceholder ? this._renderPlaceholder() : this._renderImage()}
        </div>
        {thumbnailLabel && (
          <Text
            containerStyle={{ width: this.props.sideLength }}
            containerClassName={nameContainerClasses}
            data-testid="ThumbnailPicture__name"
            ellipsis={true}
          >
            {thumbnailLabel}
          </Text>
        )}
      </div>
    );
  }
}

ThumbnailPicture.defaultProps = {
  sideLength: 120,
  crop: true,
  hideLabel: false
};

ThumbnailPicture.propTypes = {
  /**
   * Optional classname to add to the component
   */
  className: PropTypes.string,
  /**
   * The picture to render.
   * In case the picture load fails, a placeholder will be rendered instead
   */
  picture: PropTypes.shape({
    /**
     * The src of the image. Picture with no src will be rendered with the placeholder picture
     */
    src: PropTypes.string,
    /**
     * The name of the picture
     */
    name: PropTypes.string,
    /**
     * Callback to run when picture is clicked
     */
    onClick: PropTypes.func,
    /**
     * Callback to run when the picture has been loaded
     */
    onLoad: PropTypes.func,
    /**
     * Whether or not this should display an error image placeholder
     */
    error: PropTypes.bool,
    /**
     * Optional props to pass to the component's parent div when in error state
     */
    errorProps: PropTypes.object
  }),
  /**
   * The length of the picture square's side, in numbers. Defaults to 120
   */
  sideLength: PropTypes.number,
  /**
   * Error text message when an image didn't load, has priority over `locale`
   */
  errorText: PropTypes.string,
  /**
   * Indicates if the picture should be cropped to a square.
   * Otherwize, it will keep its native ratio. Defaults to true.
   */
  crop: PropTypes.bool,
  /**
   * The locale for the inner texts
   */
  locale: PropTypes.string,
  /**
   * Whether or not to show the label of the picture
   */
  hideLabel: PropTypes.bool
};

export default withLocale(
  ThumbnailPicture,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
